import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useCurrentUser } from '@hero/hooks-shared';
import {
  getAppProperties,
  getPostHogOrgProperties,
  getPostHogPersonProperties,
  getPostHogPersonPropertiesOnce,
  getUserProperties,
  type HeroAppId,
} from '@hero/core';

/**
 * Automatically identifies the current user with PostHog on login,
 * re-identifies on org switch, and resets on logout.
 *
 * `org` is the full unified `Org` doc from the auth-v2 getMe response
 * (`{ user, org?, driver? }`); it enriches the org group props with
 * fleet / members / settings data when available.
 */
export function usePostHogIdentify(app: HeroAppId) {
  const posthog = usePostHog();
  const { user, org } = useCurrentUser();
  const orgId = user?.currentSignedInOrgId?.toString();

  useEffect(() => {
    const appProps = getAppProperties(app, 'web');
    if (user) {
      posthog.identify(
        user._id.toString(),
        getPostHogPersonProperties(user, { app, platform: 'web' }),
        getPostHogPersonPropertiesOnce(app, 'web', user),
      );
      posthog.register({
        ...appProps,
        ...getUserProperties(user),
      });
      const orgProps = getPostHogOrgProperties({ user, org });
      if (orgProps && orgId) {
        posthog.group('org', orgId, orgProps);
      }
    } else {
      posthog.reset();
      posthog.register(appProps);
    }
  }, [user?._id, orgId, org?._id, posthog, app, user]);
}
